"use client"

/**
 * Configure Finance — the guided onboarding experience.
 *
 * A calm, full-page flow that asks a church plain-language questions about how it operates while, underneath, the
 * unchanged accounting engine records real double-entry data. It does NOT reimplement accounting: each step
 * frames and reuses an existing management surface (identity, accounts, funds, income/expense accounts).
 *
 * Design: editorial and flat — content lives on the page with whitespace and hierarchy, not boxes nested in
 * boxes. The reused management surfaces are the only framed elements on a step.
 *
 * Three concerns are kept apart:
 *  - The UNLOCK gate is the `financeActivatedAt` FLAG, set by Complete Finance Setup at the end of this wizard.
 *    It is what the nav, the route guard and the ledger all answer to, so none of them can disagree.
 *  - `isMinimumSetupComplete` is a STEP gate, not the unlock: it decides whether the accounts step may be
 *    advanced past. It used to double as the unlock, which let a branch with one account and no fund
 *    allocation into a full finance menu where nothing could be spent.
 *  - The wizard PROGRESS (which step to resume on) is a per-branch UX record. It never unlocks anything.
 */

import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CoinsIcon,
  LandmarkIcon,
  type LucideIcon,
  PiggyBankIcon,
  ScaleIcon,
  TagsIcon,
  UserCogIcon,
} from "lucide-react"

import { Button, HelperMessage, PageLoadingContainer } from "@ibada/ui"
import { WorkspaceStructureType } from "@ibada/shared-types/auth"

import { useTranslations } from "@/i18n"
import {
  useActiveBranchSlug,
  useActiveWorkspaceSlug,
  useTenantAuthFlow,
} from "@/features/auth"
import {
  buildBranchAdminUrl,
  extractBranchSlugFromAdminPath,
  isParishBranchAlias,
} from "@/shared/lib/branch/branch-admin-routing"
import { useApiErrorMessage } from "@/shared/hooks/use-api-error"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormScreen, MobileStepLede, MobileStepProgress } from "@/shared/components/mobile-form"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import {
  type AccountingStepHandle,
  OnboardingAccountingStep,
  OnboardingAccountsStep,
  OnboardingCategoriesStep,
  OnboardingFundsStep,
} from "@/features/finance/containers/onboarding/onboarding-steps"
import { OnboardingFundAllocationStep } from "@/features/finance/containers/onboarding/onboarding-fund-allocation-step"
import {
  type GovernanceStepHandle,
  OnboardingGovernanceStep,
} from "@/features/finance/containers/onboarding/onboarding-governance-step"
import { FinanceGovernance } from "@/features/finance/types/finance-governance"
import {
  isMinimumSetupComplete,
  isParishFirstSetupBlocked,
  useActivateFinanceMutation,
  useFinanceSetupQuery,
} from "@/features/finance/services/finance-setup-api"
import {
  FinanceOnboardingStatus,
  FinanceOnboardingStep,
  useFinanceOnboardingProgressQuery,
  useUpdateFinanceOnboardingProgressMutation,
} from "@/features/finance/services/finance-onboarding-api"

/**
 * The steps, in flow order. The intro is NOT a step.
 *
 * GOVERNANCE is conditional (ADR-0024): only the parish of a multi-branch workspace is asked, and only while
 * the choice is still open. It sits SECOND rather than last because it decides whether the accounts the later
 * steps create are global or branch-owned — asked at the end, the chart would already have been built under
 * the other assumption.
 */
const STEP_KEY_BY_STEP = {
  [FinanceOnboardingStep.Accounting]: "accounting",
  [FinanceOnboardingStep.Governance]: "governance",
  [FinanceOnboardingStep.Accounts]: "accounts",
  [FinanceOnboardingStep.Funds]: "funds",
  [FinanceOnboardingStep.Categories]: "categories",
  [FinanceOnboardingStep.FundAllocation]: "fundAllocation",
} as const

type StepKey = (typeof STEP_KEY_BY_STEP)[keyof typeof STEP_KEY_BY_STEP]

/**
 * A normal branch in a multi-branch workspace cannot even begin configuration until the parish has
 * deliberately opened its books. Reaching the parish wizard's last step is draft progress, not completion.
 */
export const isBranchSetupBlockedByParish = (status: {
  isParish?: boolean
  governanceApplies?: boolean
  parishSetupComplete?: boolean
} | null): boolean =>
  isParishFirstSetupBlocked({
    governanceApplies: Boolean(status?.governanceApplies),
    isParish: Boolean(status?.isParish),
    parishSetupComplete: Boolean(status?.parishSetupComplete),
  })

/**
 * Why Complete cannot be pressed yet — or `null` when it can.
 *
 * The VALUES are the server's own refusal codes, so the reason shown before the press and the reason returned
 * by a refused press are the same sentence out of `finance.errors.<CODE>`. Two wordings for one fact is how a
 * reader ends up believing the app is arguing with itself.
 *
 * The order matches `activate()` on the server, so whichever it would report first is the one named here.
 * The client used to weigh ONLY the allocation, which is why a branch with no financial account, or whose
 * parish had not opened its books, could be handed a live button that was always going to be refused.
 * The server remains the authority; this exists so we stop offering an action that cannot succeed.
 */
export enum FinanceCompletionBlocker {
  IdentityMissing = "FINANCE_SETUP_REQUIRED",
  ParishNotActivated = "PARISH_FINANCE_SETUP_REQUIRED",
  FinancialAccountMissing = "FINANCIAL_ACCOUNT_REQUIRED",
  OpeningAllocationIncomplete = "OPENING_ALLOCATION_INCOMPLETE",
}

export const resolveCompletionBlocker = (input: {
  /** Already activated — nothing left to check, the press only navigates. */
  alreadyActivated: boolean
  /** The accounting identity is stated (`FinanceSetupStatus.isComplete`). */
  identityComplete: boolean
  /** Mirrors the server exactly: true for a parish or a mono workspace, else the parish's activation. */
  parishSetupComplete: boolean
  /** A branch has to have said where its money lives. */
  financialAccountsCount: number
  /** The SERVER's verdict on the stored allocation. */
  allocationSatisfied: boolean
  /**
   * What is on screen balances, and this same press will store it. Without this half, Complete sat dead in
   * front of a screen reading "Remaining 0.00" until a separate Save was found first — two presses for one
   * intention.
   */
  draftBalanced: boolean
}): FinanceCompletionBlocker | null => {
  if (input.alreadyActivated) return null
  if (!input.identityComplete) return FinanceCompletionBlocker.IdentityMissing
  if (!input.parishSetupComplete) return FinanceCompletionBlocker.ParishNotActivated
  if (input.financialAccountsCount < 1) return FinanceCompletionBlocker.FinancialAccountMissing
  if (!(input.allocationSatisfied || input.draftBalanced)) {
    return FinanceCompletionBlocker.OpeningAllocationIncomplete
  }
  return null
}

/** Complete, static wizard topology for a mono-branch workspace. */
export const MONO_BRANCH_FINANCE_ONBOARDING_STEPS: readonly FinanceOnboardingStep[] = [
  FinanceOnboardingStep.Accounting,
  FinanceOnboardingStep.Accounts,
  FinanceOnboardingStep.Funds,
  FinanceOnboardingStep.Categories,
  FinanceOnboardingStep.FundAllocation,
]

/** Complete, static wizard topology for a multi-branch workspace. */
export const MULTI_BRANCH_FINANCE_ONBOARDING_STEPS: readonly FinanceOnboardingStep[] = [
  FinanceOnboardingStep.Accounting,
  FinanceOnboardingStep.Governance,
  FinanceOnboardingStep.Accounts,
  FinanceOnboardingStep.Funds,
  FinanceOnboardingStep.Categories,
  FinanceOnboardingStep.FundAllocation,
]

/** Workspace structure owns flow topology; setup status supplies values and gates, never the step array. */
export const buildStepOrderForWorkspace = (
  structureType: WorkspaceStructureType,
  routeTargetsParish: boolean,
): readonly FinanceOnboardingStep[] =>
  structureType === WorkspaceStructureType.MultiBranch && routeTargetsParish
    ? MULTI_BRANCH_FINANCE_ONBOARDING_STEPS
    : MONO_BRANCH_FINANCE_ONBOARDING_STEPS

/**
 * Onboarding must never borrow another workspace's structure while auth refreshes. The generic workspace
 * resolver intentionally falls back to another active access, which is useful for recovery navigation but
 * wrong for wizard topology: a previous multi-branch church would flash Governance inside a mono church.
 */
export const resolveOnboardingWorkspaceStructure = (
  workspaceSlug: string | null,
  workspaceAccess:
    | readonly { workspace: { slug: string; structureType: WorkspaceStructureType } }[]
    | null
    | undefined,
): WorkspaceStructureType | null => {
  if (!workspaceSlug || !workspaceAccess) return null
  return workspaceAccess.find((access) => access.workspace.slug === workspaceSlug)?.workspace.structureType ?? null
}

type OnboardingStepPlan = {
  workspaceSlug: string
  /** Frozen alongside the workspace so a parish plan cannot survive a switch to a branch route (see below). */
  routeTargetsParish: boolean
  steps: readonly FinanceOnboardingStep[]
}

/**
 * Once selected, a workspace's topology is immutable for this mounted wizard. Saving Step 1 refreshes finance
 * and authorization data; none of those intermediate states may replace a mono plan with a parish plan.
 *
 * The freeze is keyed on the workspace AND on whether the route targets the parish. Keying on the workspace
 * alone let a parish plan (which carries Governance) survive a same-workspace switch from the parish route to
 * a branch route, flashing Governance in the branch flow. A refetch that only flips the structure type is
 * still ignored — that is the transient the freeze exists to absorb.
 */
export const resolveStableOnboardingStepPlan = (
  current: OnboardingStepPlan | null,
  input: {
    workspaceSlug: string | null
    structureType: WorkspaceStructureType | null
    routeTargetsParish: boolean
  },
): OnboardingStepPlan | null => {
  if (
    current?.workspaceSlug === input.workspaceSlug &&
    current?.routeTargetsParish === input.routeTargetsParish
  ) {
    return current
  }
  if (!input.workspaceSlug || !input.structureType) return null

  return {
    workspaceSlug: input.workspaceSlug,
    routeTargetsParish: input.routeTargetsParish,
    steps: buildStepOrderForWorkspace(input.structureType, input.routeTargetsParish),
  }
}

/**
 * The concrete position of a SEMANTIC step within a resolved order.
 *
 * A step that is absent from the current order — a parish session's stored GOVERNANCE resumed in a branch
 * order that has no such step — clamps to the first step rather than mapping to a stray index. This is the
 * safety that lets the wizard track its place by meaning instead of by number: index 1 of a multi order is
 * Governance, but a semantic ACCOUNTS resolves to Accounts in every order it appears in and to Accounting in
 * the one it does not, never to Governance.
 */
export const resolveOnboardingStepIndex = (
  stepOrder: readonly FinanceOnboardingStep[],
  step: FinanceOnboardingStep,
): number => {
  const index = stepOrder.indexOf(step)
  return index >= 0 ? index : 0
}

/**
 * Where the wizard opens, kept SEMANTIC. `null` progress is "not started" → the intro. A stored step that no
 * longer exists in this order (a stale GOVERNANCE from a parish session, resumed on a branch) resolves to the
 * first step, so removing Governance from the array can never point the opening view at the wrong screen.
 */
export const resolveInitialWizardView = (
  stepOrder: readonly FinanceOnboardingStep[],
  storedStep: FinanceOnboardingStep | null | undefined,
): "intro" | FinanceOnboardingStep => {
  if (!storedStep) return "intro"
  return stepOrder.includes(storedStep) ? storedStep : (stepOrder[0] ?? "intro")
}

/**
 * The route is the authority for "am I configuring the parish or a branch?".
 *
 * Auth/branch context stores are allowed to lag behind navigation and refetches. If wizard topology reads
 * those stores, a branch URL can briefly inherit the parish shape and flash the Governance step before the
 * route state settles.
 */
export const resolveOnboardingRouteBranchSlug = (
  pathname: string,
  fallbackBranchSlug: string | null | undefined,
): string | null => extractBranchSlugFromAdminPath(pathname) ?? fallbackBranchSlug ?? null

/** A quiet visual identity per step, used on the intro's journey panel. */
const STEP_ICONS: Record<StepKey, LucideIcon> = {
  accounting: CoinsIcon,
  governance: ScaleIcon,
  accounts: LandmarkIcon,
  funds: PiggyBankIcon,
  categories: TagsIcon,
  fundAllocation: ScaleIcon,
}

export function FinanceOnboardingWizard() {
  const t = useTranslations("finance.onboarding")
  // Root translator: the blocked reason is read from `finance.errors.<CODE>`, the same copy the server's
  // refusal resolves to, so what blocks the press and what a refused press reports are one sentence.
  const tRoot = useTranslations()
  const navigate = useNavigate()
  const location = useLocation()
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const { authMe } = useTenantAuthFlow()

  const {
    workspaceId,
    activeContext,
    scopeBranch,
    canEditFinance,
    isMonoBranchWorkspace,
  } = useFinancePageState()
  const branchId = scopeBranch?.id ?? null
  const canEdit = canEditFinance
  const setupQuery = useFinanceSetupQuery(workspaceId ?? null, branchId)
  const progressQuery = useFinanceOnboardingProgressQuery(workspaceId ?? null, branchId)
  const saveProgress = useUpdateFinanceOnboardingProgressMutation(workspaceId ?? null, branchId)
  const activateFinance = useActivateFinanceMutation(workspaceId ?? null, branchId)

  const status = setupQuery.data ?? null
  const minimumComplete = isMinimumSetupComplete(status)

  /*
   * Who may add income/expense accounts here.
   *
   * The parish and a mono-branch workspace always may — they own the shared chart. A BRANCH may too, but
   * only under BRANCH_AUTONOMY, and the account it creates belongs to it alone. That half of ADR-0024 had a
   * complete backend — the create endpoint takes a branchId and the read path merges global with
   * branch-owned — and no client that ever sent one, so choosing autonomy changed what the parish could SEE
   * and nothing about what a branch could BUILD.
   */
  const branchAutonomy = status?.financeGovernance === FinanceGovernance.BranchAutonomy
  const ownsChartAsBranch = canEdit && activeContext !== "parish" && !isMonoBranchWorkspace && branchAutonomy
  const canCreateChartAccounts =
    (canEdit && (activeContext === "parish" || isMonoBranchWorkspace)) || ownsChartAsBranch
  // Non-null only when the account being created is the branch's own; the parish creates workspace-wide ones.
  const chartOwningBranchId = ownsChartAsBranch ? branchId : null

  /*
   * The workspace structure comes from the already-loaded auth context. Until that exact value exists, render
   * no wizard at all: treating "not resolved" as multi-branch was the flicker that briefly showed Governance
   * in mono workspaces. The finance setup response never participates in the step array.
   */
  const workspaceStructureType = React.useMemo(
    () => resolveOnboardingWorkspaceStructure(workspaceSlug, authMe?.workspaceAccess),
    [authMe?.workspaceAccess, workspaceSlug],
  )
  const stepPlanRef = React.useRef<OnboardingStepPlan | null>(null)
  const routeBranchSlug = React.useMemo(
    () => resolveOnboardingRouteBranchSlug(location.pathname, branchSlug),
    [branchSlug, location.pathname],
  )
  stepPlanRef.current = resolveStableOnboardingStepPlan(stepPlanRef.current, {
    workspaceSlug,
    structureType: workspaceStructureType,
    routeTargetsParish: isParishBranchAlias(routeBranchSlug),
  })
  const stepOrder = stepPlanRef.current?.steps ?? null
  const parishFirstSetupApplies = workspaceStructureType === WorkspaceStructureType.MultiBranch

  const stepKeys = React.useMemo(
    () => (stepOrder ?? []).map((step) => STEP_KEY_BY_STEP[step]),
    [stepOrder],
  )
  const stepIndexOf = React.useCallback(
    (step: FinanceOnboardingStep) => (stepOrder ? resolveOnboardingStepIndex(stepOrder, step) : 0),
    [stepOrder],
  )
  const alreadyActivated = Boolean(status?.isActivated)
  /** The SERVER's verdict on whether the opening money is fully allocated — the state as last STORED. */
  const allocationSatisfied = alreadyActivated || (status?.openingAllocation?.isSatisfied ?? false)

  /*
   * `null` = still deciding the initial view; "intro" = the pre-step welcome; a step = the ACTIVE step, held
   * SEMANTICALLY rather than as an index. A numeric index broke the moment `stepOrder` changed shape under it
   * — index 1 is Governance in the multi order and Accounts in the mono one — so a transition or recompute
   * could paint the wrong step for a frame. Tracking the meaning makes that impossible: the index is derived
   * from the step against the current order on every render.
   */
  const [view, setView] = React.useState<"intro" | FinanceOnboardingStep | null>(null)
  const didInit = React.useRef(false)

  // Step 1 owns no save button — its Continue saves it. The step reports its readiness and exposes a save handle.
  const accountingRef = React.useRef<AccountingStepHandle>(null)
  const [accountingReady, setAccountingReady] = React.useState(false)
  // Governance saves on Continue too, and will not advance on a default nobody picked.
  const governanceRef = React.useRef<GovernanceStepHandle>(null)
  const [governanceChosen, setGovernanceChosen] = React.useState(false)
  const [advancing, setAdvancing] = React.useState(false)
  const isMobile = useIsMobile()
  /*
   * What the allocation step is showing right now. The SERVER still decides whether Complete is enabled —
   * this only decides what the warning beside it SAYS, because "allocate the whole balance" is wrong advice
   * for someone who already has and simply has not pressed Save yet.
   */
  const [allocationDraft, setAllocationDraft] = React.useState({ balanced: false, dirty: false, resolved: false })
  /** The allocation step's save, so Complete can persist the draft before it activates. Null when unchanged. */
  const allocationSaveRef = React.useRef<(() => Promise<void>) | null>(null)
  /* One press now covers save + activate, so the pending label has to span both, not just the activation. */
  const [finishing, setFinishing] = React.useState(false)
  /*
   * Why the last press did not work, in the reader's language.
   *
   * Both catch sites used to swallow the failure with the note that "the shared API error toast already
   * explained why". No such toast exists — there is no global mutation error handler — so all four server
   * refusals (identity not set, parish books not open, no financial account, opening money unallocated)
   * produced a button that visibly did nothing. It is state rather than a toast because the reason is about
   * the step being looked at, and it has to survive long enough to be acted on.
   */
  const [completionError, setCompletionError] = React.useState<string | null>(null)
  const resolveErrorMessage = useApiErrorMessage("finance")

  const completionBlocker = resolveCompletionBlocker({
    alreadyActivated,
    identityComplete: Boolean(status?.isComplete),
    parishSetupComplete: status?.governanceApplies ? (status.parishSetupComplete ?? false) : true,
    financialAccountsCount: status?.financialAccountsCount ?? 0,
    allocationSatisfied,
    draftBalanced: allocationDraft.balanced,
  })
  const canCompleteSetup = completionBlocker === null
  const onAllocationDraftChange = React.useCallback(
    (next: { balanced: boolean; dirty: boolean; resolved: boolean }) => {
      setAllocationDraft((current) => {
        if (
          current.balanced === next.balanced &&
          current.dirty === next.dirty &&
          current.resolved === next.resolved
        ) {
          return current
        }
        // Editing after a refusal is an answer to it — a stale reason left standing beside a changed screen
        // is worse than none.
        setCompletionError(null)
        return next
      })
    },
    [],
  )

  /*
   * Decide the initial view ONCE — no row → intro; otherwise resume at the stored SEMANTIC step, clamped to the
   * first step if that step is absent from this order (a parish session's Governance resumed on a branch).
   *
   * Initialization waits for the frontend workspace structure and the stored progress. Finance setup status is
   * deliberately irrelevant to the flow's topology.
   */
  React.useEffect(() => {
    if (didInit.current || progressQuery.isLoading || !stepOrder) return
    didInit.current = true
    setView(resolveInitialWizardView(stepOrder, progressQuery.data?.currentStep ?? null))
  }, [progressQuery.isLoading, progressQuery.data, stepOrder])

  /*
   * Has governance already been answered? The SERVER says so, because only it can.
   *
   * Inferring this from stored progress does not work: pressing Back rewrites the stored step to Governance
   * itself, erasing the very evidence that the reader had been past it. The workspace now records WHEN the
   * choice was made, which is the only fact that survives navigating around.
   */
  const governanceAlreadyAnswered = Boolean(status?.governanceChosen)

  const persist = React.useCallback(
    (stepIndex: number, onboardingStatus: FinanceOnboardingStatus) => {
      // Unreachable before the flow's shape is settled — nothing that can call this is on screen until then.
      const step = stepOrder?.[stepIndex]
      if (!step) return
      saveProgress.mutate({ currentStep: step, status: onboardingStatus })
    },
    [saveProgress, stepOrder],
  )

  const goToDashboard = () => navigate(buildBranchAdminUrl(branchSlug, "/", workspaceSlug ?? undefined))
  const goToFinance = () => navigate(buildBranchAdminUrl(branchSlug, "/finance", workspaceSlug ?? undefined))

  /*
   * Whether this wizard has been walked before, and where it got to.
   *
   * A progress row only exists once someone has started, so its presence IS the answer. The intro is reached
   * again by going Back from the first step, and it went on saying "Start" there — and, worse, `startSetup`
   * always persisted step 0, so pressing it could quietly move somebody's stored place backwards.
   */
  const storedStepIndex = React.useMemo(
    () => (progressQuery.data && stepOrder ? stepIndexOf(progressQuery.data.currentStep) : null),
    [progressQuery.data, stepOrder, stepIndexOf],
  )
  const hasStarted = storedStepIndex !== null

  /** Begin, or pick the walk back up exactly where it was left. */
  const startSetup = () => {
    const targetIndex = storedStepIndex ?? 0
    const step = stepOrder?.[targetIndex]
    if (!step) return
    setView(step)
    persist(targetIndex, FinanceOnboardingStatus.InProgress)
  }

  const goToStep = (nextIndex: number) => {
    const step = stepOrder?.[nextIndex]
    if (!step) return
    setView(step)
    persist(nextIndex, FinanceOnboardingStatus.InProgress)
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // "Save for later": keep the flow open, leave the finance area (finance would bounce back while still locked).
  const saveForLater = (stepIndex: number) => {
    persist(stepIndex, FinanceOnboardingStatus.InProgress)
    goToDashboard()
  }

  // "Finish for now": the OPTIONAL steps are still open, so keep the flow resumable (IN_PROGRESS). A "Continue
  // setup" entry stays in the Finance nav so the user can come back to where they left off. Land in Finance.
  /**
   * Persist whatever is typed into the allocation step before navigating away from it.
   *
   * The step used to own a Save button, so leaving with unsaved figures was the reader's choice. Now that
   * Complete saves for them, every OTHER way off this step has to as well — otherwise "Finish for now" and
   * Back silently discard an opening balance someone just divided across a dozen funds.
   *
   * Returns false when the save failed, and the caller stays put: navigating away on a failed save would
   * throw the figures out with nothing on screen still holding them.
   */
  const saveAllocationDraft = async (): Promise<boolean> => {
    if (!allocationSaveRef.current) return true
    try {
      await allocationSaveRef.current()
      return true
    } catch (error) {
      setCompletionError(resolveErrorMessage(error))
      return false
    }
  }

  const finishForNow = async (stepIndex: number) => {
    if (!(await saveAllocationDraft())) return
    persist(stepIndex, FinanceOnboardingStatus.InProgress)
    goToFinance()
  }

  /** Back out of a step, keeping the allocation figures if that is the step being left. */
  const goBackFrom = async (stepIndex: number) => {
    if (!(await saveAllocationDraft())) return
    if (stepIndex === 0) setView("intro")
    else goToStep(stepIndex - 1)
  }

  /**
   * "Complete finance setup" (IBA-170): this is not a dismissal, it OPENS THE BOOKS. Until it succeeds the
   * backend refuses every operational posting, so the wizard is only marked finished once the server has
   * actually activated the branch — marking it first would leave a treasurer looking at a finished wizard and
   * a finance module that still refuses to record anything.
   */
  const finish = async (stepIndex: number) => {
    if (!alreadyActivated) {
      setFinishing(true)
      setCompletionError(null)
      try {
        // Save the allocation draft first. It used to be a second button the reader had to find and press
        // before this one came alive; the intention was always one thing — finish — so it is one press.
        if (!(await saveAllocationDraft())) return
        await activateFinance.mutateAsync()
      } catch (error) {
        // Stay on the step, and say why. The server refuses for four different reasons and each sends the
        // reader somewhere different, so "something went wrong" would not be enough even if it were shown.
        setCompletionError(resolveErrorMessage(error))
        return
      } finally {
        setFinishing(false)
      }
    }
    persist(stepIndex, FinanceOnboardingStatus.Finished)
    goToFinance()
  }

  if (!stepOrder || setupQuery.isLoading || progressQuery.isLoading || view === null) {
    return <PageLoadingContainer fullScreen={false} />
  }

  if (!workspaceId || !branchId) {
    return (
      <div className='mx-auto w-full max-w-2xl py-16 md:px-6'>
        <p className='text-lg font-medium'>{t("noBranch")}</p>
      </div>
    )
  }

  // A disabled setup query is pending-but-not-loading until its scope resolves. Keep the body closed until the
  // status required by completion and parish activation gates exists; step topology no longer depends on it.
  if (!status) {
    return <PageLoadingContainer fullScreen={false} />
  }

  if (parishFirstSetupApplies && isBranchSetupBlockedByParish(status)) {
    return (
      <div className='mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-2xl flex-col justify-center gap-6 py-16 md:px-6'>
        <HelperMessage tone='warning' size='md'>
          {tRoot("finance.errors.PARISH_FINANCE_SETUP_REQUIRED")}
        </HelperMessage>
        <div>
          <Button variant='outline' onClick={goToDashboard}>
            <ArrowLeftIcon />
            {t("actions.exit")}
          </Button>
        </div>
      </div>
    )
  }

  /* ------------------------------------- Intro (not a step) ------------------------------------- */
  if (view === "intro") {
    // Phone: a welcome in the app's style. A hero card (an icon trio, the title, why), the steps ahead as a
    // connected timeline with each step's icon, and one full-width start pill.
    if (isMobile) {
      return (
        <div className='flex flex-col gap-5 pt-2 pb-6'>
          <div className='relative overflow-hidden rounded-[1.75rem] bg-card px-5 pt-7 pb-6 text-center shadow-[0_8px_24px_-12px_rgba(16,24,40,0.12)]'>
            <div aria-hidden className='pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-primary/12 to-transparent' />
            <div aria-hidden className='relative mx-auto flex h-20 w-44 items-center justify-center'>
              <span className='absolute left-3 top-6 flex size-11 -rotate-12 items-center justify-center rounded-2xl bg-card text-primary shadow-md ring-1 ring-border/60'>
                <LandmarkIcon className='size-5' />
              </span>
              <span className='absolute right-3 top-6 flex size-11 rotate-12 items-center justify-center rounded-2xl bg-card text-primary shadow-md ring-1 ring-border/60'>
                <PiggyBankIcon className='size-5' />
              </span>
              <span className='relative flex size-16 items-center justify-center rounded-[1.25rem] bg-primary text-primary-foreground shadow-[0_10px_24px_-8px_rgba(5,150,105,0.65)]'>
                <CoinsIcon className='size-8' />
              </span>
            </div>
            <h1 className='relative mt-4 text-[1.625rem] leading-tight font-extrabold tracking-tight text-balance break-words'>{t("intro.title")}</h1>
            <p className='relative mt-2 text-[15px] leading-relaxed font-semibold text-pretty text-muted-foreground'>{t("intro.body")}</p>
          </div>

          {/* The steps ahead as compact chips (number and name), so the whole welcome fits without scrolling. */}
          <section className='flex flex-col gap-2'>
            <h2 className='px-1 text-sm font-bold text-muted-foreground'>{t("intro.overviewTitle")}</h2>
            <ol className='flex flex-wrap gap-2'>
              {stepKeys.map((key, index) => {
                const Icon = STEP_ICONS[key]
                return (
                  <li key={key} className='inline-flex h-9 max-w-full items-center gap-2 rounded-full bg-card pr-3.5 pl-1.5 ring-1 ring-border/60 ring-inset'>
                    <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                      <Icon className='size-3.5' aria-hidden='true' />
                    </span>
                    <span className='truncate text-[13px] font-bold'>
                      <span className='text-muted-foreground tabular-nums'>{index + 1}. </span>
                      {t(`steps.${key}`)}
                    </span>
                  </li>
                )
              })}
            </ol>
          </section>

          <div className='flex flex-col gap-2.5'>
            <button
              type='button'
              onClick={startSetup}
              disabled={saveProgress.isPending}
              className='flex h-[3.25rem] w-full items-center justify-center gap-2 rounded-full bg-primary text-base font-extrabold text-primary-foreground shadow-[0_8px_20px_-8px_rgba(5,150,105,0.6)] active:brightness-95 disabled:opacity-45'
            >
              {hasStarted ? t("actions.resume") : t("actions.start")}
              <ArrowRightIcon className='size-5' />
            </button>
            <p className='px-6 text-center text-xs font-semibold text-muted-foreground'>{t("intro.oneTime")}</p>
          </div>
        </div>
      )
    }

    return (
      <div className='grid min-h-[calc(100vh-8rem)] lg:grid-cols-[1.05fr_0.95fr]'>
        {/* Message + call to action. */}
        <div className='flex flex-col justify-center py-8 md:px-10 md:py-12 lg:px-14'>
          <div className='w-full max-w-md'>
            <p className='text-primary text-xs font-semibold tracking-[0.16em] uppercase'>{t("intro.eyebrow")}</p>
            <h1 className='mt-4 text-4xl font-semibold tracking-tight text-balance sm:text-5xl'>
              {t("intro.title")}
            </h1>
            <p className='text-muted-foreground mt-5 text-base leading-relaxed text-pretty'>{t("intro.body")}</p>

            <HelperMessage tone='warning' size='md' icon={UserCogIcon} className='mt-6'>
              <span className='font-medium'>{t("intro.whoTitle")}</span> {t("intro.whoBody")}
            </HelperMessage>

            <div className='mt-9 flex flex-wrap items-center gap-x-5 gap-y-3'>
              <Button size='lg' onClick={startSetup} disabled={saveProgress.isPending}>
                {hasStarted ? t("actions.resume") : t("actions.start")}
                <ArrowRightIcon />
              </Button>
              <span className='text-muted-foreground text-xs'>{t("intro.oneTime")}</span>
            </div>
          </div>
        </div>

        {/* The journey — a calm, faintly-tinted bordered card showing the four steps as a quiet timeline. */}
        <div className='bg-primary/[0.04] relative m-6 hidden flex-col justify-center overflow-hidden rounded-2xl border px-10 py-12 lg:flex'>
          {/* One faint oversized mark for depth — no gradients, no glow. */}
          <CoinsIcon
            className='text-primary/[0.05] pointer-events-none absolute -top-8 -right-8 size-56'
            aria-hidden='true'
          />
          <p className='text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase'>
            {t("intro.overviewTitle")}
          </p>
          <ol className='mt-8 space-y-1'>
            {stepKeys.map((key, index) => {
              const Icon = STEP_ICONS[key]
              const last = index === stepKeys.length - 1
              return (
                <li key={key} className='flex gap-4'>
                  <div className='flex flex-col items-center'>
                    <span className='bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-xl'>
                      <Icon className='size-5' aria-hidden='true' />
                    </span>
                    {!last ? <span className='bg-border my-1 w-px flex-1' /> : null}
                  </div>
                  <div className={last ? "" : "pb-6"}>
                    <p className='leading-tight font-medium'>{t(`steps.${key}`)}</p>
                    <p className='text-muted-foreground mt-1 text-sm leading-relaxed'>
                      {t(`intro.overview.${key}`)}
                    </p>
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      </div>
    )
  }

  /* ----------------------------------------- Steps ----------------------------------------- */
  // `view` holds the step SEMANTICALLY; its position is resolved against the current order every render, so a
  // step that is not in this order clamps to the first rather than pointing at whatever sits at a stale index.
  const stepIndex = resolveOnboardingStepIndex(stepOrder, view)
  // Keyed, not positional: GOVERNANCE is inserted at position 1 when it applies, so every `stepIndex === 1`
  // below would otherwise have silently come to mean a different step for a parish than for a branch.
  const stepKey = stepKeys[stepIndex] ?? stepKeys[0]
  const isLast = stepIndex === stepOrder.length - 1

  // Continue gating: required steps advance only once their real accounting data exists; optional steps are
  // free. Governance is required too — it is a choice, and the wizard will not carry a default past it.
  const canContinue =
    stepKey === "accounting"
      ? accountingReady
      : stepKey === "governance"
        ? governanceChosen
        : stepKey === "accounts"
          ? minimumComplete
          : true

  // The accounting step owns no save button — its Continue saves the identity, then advances.
  const handleContinue = async () => {
    if (stepKey === "governance") {
      setAdvancing(true)
      const ok = await governanceRef.current?.save()
      setAdvancing(false)
      if (!ok) return
    }
    if (stepKey === "accounting") {
      setAdvancing(true)
      const ok = await accountingRef.current?.save()
      setAdvancing(false)
      if (!ok) return
    }
    goToStep(stepIndex + 1)
  }

  const stepBody = (
    <StepBody
      stepKey={stepKey}
      context={activeContext}
      workspaceId={workspaceId}
      branchId={branchId}
      canEdit={canEdit}
      canCreateChartAccounts={canCreateChartAccounts}
      chartOwningBranchId={chartOwningBranchId}
      parishFirstSetupApplies={parishFirstSetupApplies}
      accountingRef={accountingRef}
      onAccountingReadyChange={setAccountingReady}
      governanceRef={governanceRef}
      governance={status?.financeGovernance ?? FinanceGovernance.FullParish}
      governanceAlreadyAnswered={governanceAlreadyAnswered}
      onGovernanceChosenChange={setGovernanceChosen}
      onAllocationDraftChange={onAllocationDraftChange}
      allocationSaveRef={allocationSaveRef}
    />
  )
  const blockerMessage =
    isLast && allocationDraft.resolved && completionBlocker ? tRoot(`finance.errors.${completionBlocker}` as never) : null
  const savesForLater = stepKey === "accounting" || stepKey === "governance" || stepKey === "accounts"

  // Phone: the app's full-screen form. Retour / the wizard's name / Plus tard on top, the step's progress and
  // lede, the step's fields as grouped rows, and Continue (or Complete) as the pill pinned at the bottom.
  if (isMobile) {
    return (
      <MobileFormScreen
        // The step count, not the wizard's name: the name repeated on every step said nothing new.
        title={t("stepCount", { current: stepIndex + 1, total: stepOrder.length })}
        cancelLabel={t("actions.back")}
        onCancel={() => void goBackFrom(stepIndex)}
        saveLabel={t("mobile.later")}
        onSave={() => (savesForLater ? saveForLater(stepIndex) : void finishForNow(stepIndex))}
        saveDisabled={saveProgress.isPending}
        submitLabel={isLast ? (finishing ? t("actions.completing") : t("actions.completeSetup")) : t("actions.continue")}
        onSubmit={() => void (isLast ? finish(stepIndex) : handleContinue())}
        submitDisabled={
          isLast ? !canCompleteSetup || saveProgress.isPending || finishing : !canContinue || saveProgress.isPending || advancing
        }
        saving={advancing || finishing}
        header={
          <MobileStepProgress
            steps={stepOrder.map((step, position) => ({ key: step, label: t(`steps.${stepKeys[position]}` as never) }))}
            current={stepIndex}
            onStepPress={goToStep}
          />
        }
      >
        <MobileStepLede key={stepKey} title={t(`${stepKey}.title`)} body={t(`${stepKey}.body`)} moreLabel={t("mobile.more")} lessLabel={t("mobile.less")} />
        {stepBody}
        {blockerMessage ? (
          <HelperMessage tone='warning' size='md'>
            {blockerMessage}
          </HelperMessage>
        ) : null}
        {completionError ? (
          <HelperMessage tone='danger' size='md'>
            {completionError}
          </HelperMessage>
        ) : null}
      </MobileFormScreen>
    )
  }

  return (
    <div className='mx-auto w-full max-w-3xl py-6 md:px-6 md:py-12'>
      {/* Slim progress — a line and a count, not a box. */}
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <span className='text-muted-foreground text-xs font-semibold tracking-[0.12em] uppercase'>
            {t("title")}
          </span>
          <span className='text-muted-foreground text-xs tabular-nums'>
            {t("stepCount", { current: stepIndex + 1, total: stepOrder.length })}
          </span>
        </div>
        {/*
          The bar is also the way back. Nothing here is committed until Complete Finance Setup, so a step
          already passed must stay open to a second look — and reaching step five to fix a typo on step one
          used to mean pressing Back four times. Only backwards: going forward still runs each step's own
          gate, so the bar cannot be used to skip one.
        */}
        <div className='flex gap-1.5'>
          {stepOrder.map((step, position) => {
            const reached = position <= stepIndex
            return (
              <button
                key={step}
                type='button'
                onClick={() => (reached ? goToStep(position) : undefined)}
                disabled={!reached}
                aria-current={position === stepIndex ? "step" : undefined}
                aria-label={t(`steps.${stepKeys[position]}` as never)}
                title={t(`steps.${stepKeys[position]}` as never)}
                className={`h-1 flex-1 rounded-full transition-opacity ${
                  reached ? "bg-primary cursor-pointer hover:opacity-70" : "bg-muted cursor-default"
                }`}
              />
            )
          })}
        </div>
      </div>

      {/* Step lede — page-level heading, no card. */}
      <div className='mt-8'>
        <h1 className='text-2xl font-semibold tracking-tight'>{t(`${stepKey}.title`)}</h1>
        <p className='text-muted-foreground mt-2 max-w-prose leading-relaxed'>{t(`${stepKey}.body`)}</p>
      </div>

      <div className='mt-8'>
        {stepBody}
      </div>

      {/*
        Why the last button is dead — explained only by a `title` tooltip before, which never appears on a
        touch device and is easy to miss on a desktop.

        It names WHICH precondition is missing, because the four send the reader to four different places and
        only one of them is on this screen. Saying "allocate the opening balance" to a branch waiting on its
        parish is worse than saying nothing: it describes work that will not help.

        It carries no amount. The live figure is the step's own "Remaining"; this reads the SERVER's
        allocation, which only moves on save, so quoting it put two different numbers for one fact on the
        same screen.
      */}
      {blockerMessage ? (
        <HelperMessage tone='warning' size='md' className='mt-8'>
          {blockerMessage}
        </HelperMessage>
      ) : null}

      {/*
        The server's refusal, where the press happened. Not a toast: this is the reason a deliberate,
        end-of-flow action did not take, and it has to stay on screen while the reader goes and fixes it.
      */}
      {completionError ? (
        <HelperMessage tone='danger' size='md' className='mt-8'>
          {completionError}
        </HelperMessage>
      ) : null}

      {/*
        Actions — a plain row on the page on desktop. On a phone it is pinned above the tab bar (the scroller's
        bottom padding is the tab-bar offset, hence the matching negative inset), with the main action on its
        own full-width row: "Terminer la configuration financière" does not share a phone row with two others.
      */}
      <div className='max-md:bg-[var(--app-canvas)]/95 max-md:sticky max-md:bottom-[calc(-1*var(--app-shell-mobile-bottom-nav-offset,1.5rem))] max-md:z-20 max-md:-mx-4 max-md:mt-8 max-md:grid max-md:grid-cols-2 max-md:gap-x-2 max-md:gap-y-3 max-md:border-t max-md:px-4 max-md:pt-3 max-md:pb-[calc(var(--app-shell-mobile-bottom-nav-offset,1.5rem)+0.5rem)] max-md:backdrop-blur md:mt-12 md:flex md:items-center md:gap-2 md:border-t md:pt-6'>
        {isLast ? (
          <Button
            className='max-md:col-span-2 max-md:w-full md:order-last'
            onClick={() => void finish(stepIndex)}
            /*
             * Enabled on what the reader can SEE balancing, not only on what the server has already stored:
             * the draft is saved by this same press, so gating it on the saved figure would leave the button
             * dead in front of a screen that reads "Remaining 0.00".
             */
            disabled={!canCompleteSetup || saveProgress.isPending || finishing}
            title={completionBlocker ? tRoot(`finance.errors.${completionBlocker}` as never) : undefined}
          >
            {finishing ? t("actions.completing") : t("actions.completeSetup")}
          </Button>
        ) : (
          <Button
            className='max-md:col-span-2 max-md:w-full md:order-last'
            onClick={() => void handleContinue()}
            disabled={!canContinue || saveProgress.isPending || advancing}
          >
            {t("actions.continue")}
            <ArrowRightIcon />
          </Button>
        )}

        {/* On the first step, Back returns to the welcome page (you haven't gone anywhere yet). */}
        <Button
          variant='ghost'
          className='max-md:justify-self-start md:mr-auto max-md:underline max-md:decoration-muted-foreground/50 max-md:underline-offset-4'
          onClick={() => void goBackFrom(stepIndex)}
          disabled={saveProgress.isPending}
        >
          <ArrowLeftIcon />
          {stepIndex === 0 ? t("actions.backToWelcome") : t("actions.back")}
        </Button>

        {savesForLater ? (
          <Button variant='ghost' className='max-md:justify-self-end max-md:underline max-md:decoration-muted-foreground/50 max-md:underline-offset-4' onClick={() => saveForLater(stepIndex)} disabled={saveProgress.isPending}>
            {t("actions.saveLater")}
          </Button>
        ) : (
          <Button variant='ghost' className='max-md:justify-self-end max-md:underline max-md:decoration-muted-foreground/50 max-md:underline-offset-4' onClick={() => void finishForNow(stepIndex)} disabled={saveProgress.isPending}>
            {t("actions.finishNow")}
          </Button>
        )}
      </div>
    </div>
  )
}

/** A step's tip: a banner above the list on desktop, a small grey note under it on a phone. */
function StepTip({ text, children }: { text: string; children: React.ReactNode }) {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <div className='flex flex-col gap-2'>
        {children}
        <p className='px-4 text-xs font-semibold text-muted-foreground'>{text}</p>
      </div>
    )
  }
  return (
    <div className='space-y-5'>
      <HelperMessage tone='info' size='md'>
        {text}
      </HelperMessage>
      {children}
    </div>
  )
}

function StepBody({
  stepKey,
  context,
  workspaceId,
  branchId,
  canEdit,
  canCreateChartAccounts,
  chartOwningBranchId,
  parishFirstSetupApplies,
  accountingRef,
  onAccountingReadyChange,
  governanceRef,
  governance,
  governanceAlreadyAnswered,
  onGovernanceChosenChange,
  onAllocationDraftChange,
  allocationSaveRef,
}: {
  stepKey: StepKey
  context: "branch" | "parish"
  workspaceId: string
  branchId: string
  canEdit: boolean
  canCreateChartAccounts: boolean
  chartOwningBranchId: string | null
  parishFirstSetupApplies: boolean
  onAllocationDraftChange: (state: { balanced: boolean; dirty: boolean; resolved: boolean }) => void
  allocationSaveRef: React.MutableRefObject<(() => Promise<void>) | null>
  accountingRef: React.Ref<AccountingStepHandle>
  onAccountingReadyChange: (ready: boolean) => void
  governanceRef: React.Ref<GovernanceStepHandle>
  governance: FinanceGovernance
  governanceAlreadyAnswered: boolean
  onGovernanceChosenChange: (chosen: boolean) => void
}) {
  const t = useTranslations("finance.onboarding")

  const stepProps = {
    context,
    workspaceId,
    branchId,
    canEdit,
    canCreateChartAccounts,
    chartOwningBranchId,
    parishFirstSetupApplies,
  }

  switch (stepKey) {
    case "accounting":
      return (
        <OnboardingAccountingStep {...stepProps} ref={accountingRef} onReadyChange={onAccountingReadyChange} />
      )
    case "governance":
      return (
        <OnboardingGovernanceStep
          ref={governanceRef}
          workspaceId={workspaceId}
          branchId={branchId}
          canEdit={canEdit}
          value={governance}
          alreadyAnswered={governanceAlreadyAnswered}
          onChosenChange={onGovernanceChosenChange}
        />
      )
    case "accounts":
      return (
        <StepTip text={t("accounts.openingExplain")}>
          <OnboardingAccountsStep {...stepProps} />
        </StepTip>
      )
    case "funds":
      return (
        <StepTip text={t("funds.examples")}>
          <OnboardingFundsStep {...stepProps} />
        </StepTip>
      )
    case "categories":
      return (
        <StepTip text={t("categories.examples")}>
          <OnboardingCategoriesStep {...stepProps} />
        </StepTip>
      )
    case "fundAllocation":
      return (
        <OnboardingFundAllocationStep
          workspaceId={workspaceId}
          branchId={branchId}
          canEdit={canEdit}
          onDraftStateChange={onAllocationDraftChange}
          saveRef={allocationSaveRef}
        />
      )
  }
}
